import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import type { InputType } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  type ReviewFormReport,
  type ReviewFormReporterSection as IReviewFormReporterSection,
  setReviewFormReporterSection,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Button } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

const reportInputs = (): Record<string, InputType> => ({
  title: {
    title: 'Title',
    inputType: 'text',
    required: true,
  },
  content: {
    title: 'Content',
    inputType: 'textarea',
    required: true,
    rows: 6,
  },
})

const newReportId = () => `report_${Date.now()}_${Math.floor(Math.random() * 1e6)}`

export const ReviewFormReportDetails = (props: {
  defaults: ReviewFormReport | null
  reporterData: IReviewFormReporterSection
  setReporterData: (data: IReviewFormReporterSection) => void
  onBack: () => void
}) => {
  const { defaults, reporterData, setReporterData, onBack } = props
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, InputType>>({})

  useEffect(() => {
    const newInputs = reportInputs()
    if (defaults) {
      newInputs.title.value = defaults.title
      newInputs.content.value = defaults.content
    }
    setInputs(newInputs)
  }, [defaults])

  const orderNo =
    defaults && reporterData.reports.findIndex((r) => r.id === defaults.id) >= 0
      ? reporterData.reports.findIndex((r) => r.id === defaults.id) + 1
      : 0

  const onChange = (key: string, value: string) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const onSubmit = async () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      const error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (!disabled && value !== undefined) data[key] = value
    }
    if (hasError) {
      setInputs(newStats)
      return
    }

    const reports = cloneDeep(reporterData.reports)
    let newReport: ReviewFormReport
    if (defaults) {
      const index = reports.findIndex((r) => r.id === defaults.id)
      if (index === -1) return
      newReport = { ...defaults, title: data.title, content: data.content || '' }
      reports[index] = newReport
    } else {
      newReport = { id: newReportId(), title: data.title, content: data.content || '' }
      reports.push(newReport)
    }

    const next: IReviewFormReporterSection = { ...reporterData, reports }

    setLoading(true)
    try {
      await setReviewFormReporterSection(next)
      setReporterData(next)
      toast(defaults ? 'Report updated.' : 'New report added.', { type: 'success' })
      onBack()
    } catch {
      setLoading(false)
    }
  }

  return (
    <div className="review-form-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Reviewer's Report - {!defaults ? 'Add' : `Update No ${orderNo}`}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-2" /> <p>Return to Reviewer's Report</p>
        </div>
      </Button>

      <div className="grid gap-4 grid-cols-1 mb-3">
        {Object.keys(inputs).map((key, index) => {
          const input = inputs[key]
          return (
            <div className="input" key={index}>
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>
      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{defaults ? 'Update' : 'Add'}</>
        </Button>
      </div>
    </div>
  )
}
